import {sendCustomMetric} from '@github-ui/stats'
import {registerStatsAccumulator, requestStatsDelivery} from '@github-ui/stats/accumulators'

type PolicyMetricKey =
  | 'TRUSTED_TYPES_POLICY_CALLED'
  | 'TRUSTED_TYPES_POLICY_ERROR'
  | 'TRUSTED_TYPES_POLICY_INITIALIZED_TWICE'

const pendingMintMetricFlushes = new Map<() => void, () => void>()

registerStatsAccumulator(
  () => {
    for (const flush of pendingMintMetricFlushes.keys()) flush()
  },
  () => {
    for (const discard of pendingMintMetricFlushes.values()) discard()
    pendingMintMetricFlushes.clear()
  },
)

export function createPolicyMetricEmitter(policyName: string): (name: PolicyMetricKey) => void {
  const tags = {trusted_types_policy_name: policyName}
  let metricsDisabled = false
  let successfulMints = 0

  function disableMetrics() {
    metricsDisabled = true
    pendingMintMetricFlushes.delete(flushSuccessfulMints)
    successfulMints = 0
  }

  function sendMetric(name: PolicyMetricKey, value: number) {
    if (metricsDisabled) return
    try {
      sendCustomMetric({name, value, tags})
    } catch {
      // Telemetry must not affect minting. Stop retrying a broken sink on every
      // use of this policy, including hot paths such as HTML rendering.
      disableMetrics()
    }
  }

  function flushSuccessfulMints() {
    pendingMintMetricFlushes.delete(flushSuccessfulMints)
    if (successfulMints === 0 || metricsDisabled) return

    const count = successfulMints
    successfulMints = 0
    sendMetric('TRUSTED_TYPES_POLICY_CALLED', count)
  }

  function discardSuccessfulMints() {
    successfulMints = 0
  }

  return name => {
    if (metricsDisabled) return

    if (name === 'TRUSTED_TYPES_POLICY_CALLED') {
      successfulMints++
      if (successfulMints === 1) {
        pendingMintMetricFlushes.set(flushSuccessfulMints, discardSuccessfulMints)
        try {
          requestStatsDelivery()
        } catch {
          // Scheduling failures must not change a successful mint.
          disableMetrics()
        }
      }
      return
    }

    sendMetric(name, 1)
  }
}
